import React, { useMemo, useEffect } from 'react';
import { Row, Col, Alert } from 'react-bootstrap';
import ColumnHeaderSelectSort from 'components/DesignSystem/ColumnHeaderSelectSort';
import LoadingSpinner from 'components/LoadingSpinner';
import { SortDirection } from 'constants/sorting';
import Button from 'components/DesignSystem/Button';
import {
  getClimateIndexLabel,
  getAllLabelsForIndex,
} from 'helper/Meteorology/climateIndicesLabels';
import { useGetClimateIndicesQuery } from 'services/api/Meteorology/Weather/Similarity';
import { useDispatch, useSelector } from 'react-redux';
import {
  setTableData,
  selectSimilaridadeTableData,
  selectSimilaridadeMapsData,
  setAnos,
  setMeses,
  clearMaps,
} from 'redux/reducers/similaridadeReducer';
import 'components/NewHome/Table/index.scss';

import './index.scss';

export default function DataTableSection() {
  const dispatch = useDispatch();
  const { data: apiData = [], isLoading, isError } = useGetClimateIndicesQuery();

  const tableData = useSelector(selectSimilaridadeTableData);
  const mapsData = useSelector(selectSimilaridadeMapsData);

  // Atualizar Redux quando dados da API chegarem
  useEffect(() => {
    if (apiData && apiData.length > 0) {
      dispatch(setTableData(apiData));
    }
  }, [apiData, dispatch]);

  const data = tableData.length > 0 ? tableData : apiData;

  // Estados de filtro
  const [selectedYears, setSelectedYears] = React.useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = React.useState<string>('');
  const [selectedNino34, setSelectedNino34] = React.useState<string[]>([]);
  const [selectedIod, setSelectedIod] = React.useState<string[]>([]);
  const [selectedTna, setSelectedTna] = React.useState<string[]>([]);
  const [selectedTsa, setSelectedTsa] = React.useState<string[]>([]);
  const [selectedAmo, setSelectedAmo] = React.useState<string[]>([]);
  const [selectedPdo, setSelectedPdo] = React.useState<string[]>([]);
  const [selectedAao, setSelectedAao] = React.useState<string[]>([]);

  // Estados para ordenação
  const [sortField, setSortField] = React.useState<string | undefined>();
  const [sortDirection, setSortDirection] = React.useState<SortDirection | undefined>();

  // Opções para os filtros
  const yearOptions = useMemo(() => {
    const uniqueYears = Array.from(new Set(data.map((item: any) => item.ano)))
      .sort((a: any, b: any) => b - a);
    return uniqueYears.map((year) => ({ value: String(year), label: String(year) }));
  }, [data]);

  const monthOptions = useMemo(() => Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1),
  })), []);

  const nino34Labels = useMemo(() => getAllLabelsForIndex('nino3.4').map((label) => ({ value: label, label })), []);
  const aaoLabels = useMemo(() => getAllLabelsForIndex('aao').map((label) => ({ value: label, label })), []);
  const defaultIndexLabels = useMemo(() => getAllLabelsForIndex('default').map((label) => ({ value: label, label })), []);

  useEffect(() => {
    dispatch(clearMaps());
  }, [
    selectedYears,
    selectedMonth,
    selectedNino34,
    selectedIod,
    selectedTna,
    selectedTsa,
    selectedAmo,
    selectedPdo,
    selectedAao,
    sortField,
    sortDirection,
    dispatch,
  ]);

  const handleSort = (field: string) => (direction?: SortDirection) => {
    setSortField(field);
    setSortDirection(direction);
  };

  // Filtrar e ordenar dados
  const processedData = useMemo(() => {
    let filtered = [...data];

    // Filtrar apenas linhas que têm mapas gerados
    if (mapsData.length > 0) {
      filtered = filtered.filter((item) => mapsData.some(
        (map) => map.ano === item.ano && map.mes === item.mes,
      ));
    }

    if (selectedYears.length > 0) {
      filtered = filtered.filter((item) => selectedYears.includes(String(item.ano)));
    }

    if (selectedMonth) {
      filtered = filtered.filter((item) => String(item.mes) === selectedMonth);
    }

    if (selectedNino34.length > 0) {
      filtered = filtered.filter((item) => {
        const label = getClimateIndexLabel(item['nino3.4'], 'nino3.4');
        return selectedNino34.includes(label);
      });
    }

    if (selectedIod.length > 0) {
      filtered = filtered.filter((item) => {
        const label = getClimateIndexLabel(item.iod, 'default');
        return selectedIod.includes(label);
      });
    }

    if (selectedTna.length > 0) {
      filtered = filtered.filter((item) => {
        const label = getClimateIndexLabel(item.tna, 'default');
        return selectedTna.includes(label);
      });
    }

    if (selectedTsa.length > 0) {
      filtered = filtered.filter((item) => {
        const label = getClimateIndexLabel(item.tsa, 'default');
        return selectedTsa.includes(label);
      });
    }

    if (selectedAmo.length > 0) {
      filtered = filtered.filter((item) => {
        const label = getClimateIndexLabel(item.amo, 'default');
        return selectedAmo.includes(label);
      });
    }

    if (selectedPdo.length > 0) {
      filtered = filtered.filter((item) => {
        const label = getClimateIndexLabel(item.pdo, 'default');
        return selectedPdo.includes(label);
      });
    }

    if (selectedAao.length > 0) {
      filtered = filtered.filter((item) => {
        const label = getClimateIndexLabel(item.aao, 'aao');
        return selectedAao.includes(label);
      });
    }

    // Ordenação
    if (sortField && sortDirection) {
      filtered = [...filtered].sort((a: any, b: any) => {
        let comparison = 0;

        if (sortField === 'Ano') {
          comparison = a.ano - b.ano;
        } else if (sortField === 'Mês') {
          comparison = a.mes - b.mes;
        } else if (sortField === 'NIÑO 3.4') {
          comparison = a['nino3.4'] - b['nino3.4'];
        } else if (sortField === 'IOD') {
          comparison = a.iod - b.iod;
        } else if (sortField === 'TNA') {
          comparison = a.tna - b.tna;
        } else if (sortField === 'TSA') {
          comparison = a.tsa - b.tsa;
        } else if (sortField === 'AMO') {
          comparison = a.amo - b.amo;
        } else if (sortField === 'PDO') {
          comparison = a.pdo - b.pdo;
        } else if (sortField === 'AAO') {
          comparison = a.aao - b.aao;
        }

        return sortDirection === 'ASC' ? comparison : -comparison;
      });
    }

    return filtered;
  }, [
    data,
    mapsData,
    selectedYears,
    selectedMonth,
    selectedNino34,
    selectedIod,
    selectedTna,
    selectedTsa,
    selectedAmo,
    selectedPdo,
    selectedAao,
    sortField,
    sortDirection,
  ]);

  // Atualizar anos e meses no Redux quando os dados filtrados mudarem
  useEffect(() => {
    if (processedData.length > 0) {
      const anos = Array.from(new Set(processedData.map((item: any) => item.ano)));
      dispatch(setAnos(anos));
    }

    if (!selectedMonth) {
      dispatch(setMeses([]));
    } else {
      dispatch(setMeses([Number(selectedMonth)]));
    }
  }, [processedData, selectedMonth, dispatch]);

  const clearFilters = () => {
    setSelectedYears([]);
    setSelectedNino34([]);
    setSelectedIod([]);
    setSelectedTna([]);
    setSelectedTsa([]);
    setSelectedAmo([]);
    setSelectedPdo([]);
    setSelectedAao([]);
    setSelectedMonth('');
    dispatch(clearMaps());
  };

  if (isLoading) {
    return (
      <div className="text-center p-4">
        <LoadingSpinner />
        <p>Carregando dados históricos...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="danger" className="m-3">
        Erro ao carregar dados dos índices climáticos. Por favor, tente novamente.
      </Alert>
    );
  }

  return (
    <div className="mb-4">
      <h4 className="mb-3">Dados Históricos dos Índices Climáticos</h4>
      {/* Botão de Limpar Filtros */}
      <Row className="d-flex justify-content-end">
        <Col md="2" className="d-flex justify-content-end me-3">
          <Button
            type="tertiary"
            icon="no_filter"
            onClick={clearFilters}
          >
            Limpar Filtros
          </Button>
        </Col>
      </Row>
      <div className="hi-table-container">

        {/* Header da tabela */}
        <div className="hi-table-header">
          <Row className="mb-1 mt-2 ht-row flex-nowrap" style={{ minWidth: 'max-content' }}>
            <ColumnHeaderSelectSort
              md={2}
              className="bg-white hi-col"
              title="Ano"
              value={selectedYears}
              setValue={setSelectedYears}
              onSort={handleSort('Ano')}
              sortDirection={sortField === 'Ano' ? sortDirection : undefined}
            >
              {yearOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </ColumnHeaderSelectSort>

            <ColumnHeaderSelectSort
              md={2}
              className="bg-white hi-col"
              title="Mês"
              value={selectedMonth}
              setValue={setSelectedMonth}
              onSort={handleSort('Mês')}
              sortDirection={sortField === 'Mês' ? sortDirection : undefined}
            >
              {monthOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </ColumnHeaderSelectSort>

            <ColumnHeaderSelectSort
              md={2}
              className="bg-white hi-col"
              title="NIÑO 3.4"
              value={selectedNino34}
              setValue={setSelectedNino34}
              onSort={handleSort('NIÑO 3.4')}
              sortDirection={sortField === 'NIÑO 3.4' ? sortDirection : undefined}
            >
              {nino34Labels.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </ColumnHeaderSelectSort>

            <ColumnHeaderSelectSort
              md={2}
              className="bg-white hi-col"
              title="IOD"
              value={selectedIod}
              setValue={setSelectedIod}
              onSort={handleSort('IOD')}
              sortDirection={sortField === 'IOD' ? sortDirection : undefined}
            >
              {defaultIndexLabels.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </ColumnHeaderSelectSort>

            <ColumnHeaderSelectSort
              md={2}
              className="bg-white hi-col"
              title="TNA"
              value={selectedTna}
              setValue={setSelectedTna}
              onSort={handleSort('TNA')}
              sortDirection={sortField === 'TNA' ? sortDirection : undefined}
            >
              {defaultIndexLabels.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </ColumnHeaderSelectSort>

            <ColumnHeaderSelectSort
              md={2}
              className="bg-white hi-col"
              title="TSA"
              value={selectedTsa}
              setValue={setSelectedTsa}
              onSort={handleSort('TSA')}
              sortDirection={sortField === 'TSA' ? sortDirection : undefined}
            >
              {defaultIndexLabels.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </ColumnHeaderSelectSort>

            <ColumnHeaderSelectSort
              md={2}
              className="bg-white hi-col"
              title="AMO"
              value={selectedAmo}
              setValue={setSelectedAmo}
              onSort={handleSort('AMO')}
              sortDirection={sortField === 'AMO' ? sortDirection : undefined}
            >
              {defaultIndexLabels.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </ColumnHeaderSelectSort>

            <ColumnHeaderSelectSort
              md={2}
              className="bg-white hi-col"
              title="PDO"
              value={selectedPdo}
              setValue={setSelectedPdo}
              onSort={handleSort('PDO')}
              sortDirection={sortField === 'PDO' ? sortDirection : undefined}
            >
              {defaultIndexLabels.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </ColumnHeaderSelectSort>

            <ColumnHeaderSelectSort
              md={2}
              className="bg-white hi-col"
              title="AAO"
              value={selectedAao}
              setValue={setSelectedAao}
              onSort={handleSort('AAO')}
              sortDirection={sortField === 'AAO' ? sortDirection : undefined}
            >
              {aaoLabels.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </ColumnHeaderSelectSort>
          </Row>
        </div>

        {/* Dados da tabela */}
        <div>
          {processedData.length > 0 ? (
            processedData.map((row) => (
              <Row
                key={`${row.ano}-${row.mes}`}
                className="pt-3 pb-3 ht-row flex-nowrap"
                style={{ minWidth: 'max-content' }}
              >
                <Col md={2} className="home-table-entry-attr hi-col ">
                  {row.ano}
                </Col>
                <Col md={2} className="home-table-entry-attr hi-col ">
                  {row.mes}
                </Col>
                <Col md={2} className="home-table-entry-attr hi-col ">
                  {row['nino3.4']?.toFixed(2)}
                </Col>
                <Col md={2} className="home-table-entry-attr hi-col ">
                  {row.iod?.toFixed(2)}
                </Col>
                <Col md={2} className="home-table-entry-attr hi-col ">
                  {row.tna?.toFixed(2)}
                </Col>
                <Col md={2} className="home-table-entry-attr hi-col ">
                  {row.tsa?.toFixed(2)}
                </Col>
                <Col md={2} className="home-table-entry-attr hi-col ">
                  {row.amo?.toFixed(2)}
                </Col>
                <Col md={2} className="home-table-entry-attr hi-col ">
                  {row.pdo?.toFixed(2)}
                </Col>
                <Col md={2} className="home-table-entry-attr hi-col ">
                  {row.aao?.toFixed(2)}
                </Col>
              </Row>
            ))
          ) : (
            <Row className="pt-3 pb-3 ht-row">
              <Col className="text-center text-muted">
                Nenhum dado encontrado com os filtros selecionados
              </Col>
            </Row>
          )}
        </div>
      </div>
    </div>
  );
}
